import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { EntityThumbnail } from '../components/EntityImage';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Target,
  Plus,
  Grid3X3,
  List,
  Filter,
  X,
} from 'lucide-react';

const FIREARM_TYPES = ['Pistol', 'Rifle', 'Shotgun', 'Other'];
const COMMON_CALIBERS = ['9mm', '.45 ACP', '.22 LR', '5.56 NATO', '.308 WIN', '12 Gauge', '.40 S&W', '.380 ACP'];

export default function FirearmsPage() {
  const navigate = useNavigate();
  const [firearms, setFirearms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [caliberFilter, setCaliberFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');

  const fetchFirearms = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (typeFilter) params.append('type', typeFilter);
      if (caliberFilter) params.append('caliber', caliberFilter);
      if (tagFilter) params.append('tag', tagFilter);

      const response = await api.get(`/firearms?${params.toString()}`);
      setFirearms(response.data);
    } catch (error) {
      console.error('Error fetching firearms:', error);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, typeFilter, caliberFilter, tagFilter]);

  useEffect(() => {
    fetchFirearms();
  }, [fetchFirearms]);

  const clearFilters = () => {
    setTypeFilter('');
    setCaliberFilter('');
    setTagFilter('');
    setSearchQuery('');
  };

  const hasActiveFilters = typeFilter || caliberFilter || tagFilter;

  const getTagColor = (tag) => {
    const colors = {
      'EDC': 'bg-secondary/20 text-secondary border-secondary/30',
      'Range': 'bg-accent/20 text-accent border-accent/30',
      'Home Defense': 'bg-destructive/20 text-destructive border-destructive/30',
      'Collector': 'bg-primary/20 text-primary border-primary/30',
    };
    return colors[tag] || 'bg-muted text-muted-foreground border-muted';
  };

  // Get unique calibers from firearms
  const uniqueCalibers = [...new Set(firearms.map(f => f.caliber))].filter(Boolean);
  const allCalibers = [...new Set([...COMMON_CALIBERS, ...uniqueCalibers])].sort();

  // Get unique tags
  const uniqueTags = [...new Set(firearms.flatMap(f => f.tags || []))].filter(Boolean);

  return (
    <Layout
      title="Firearms"
      showSearch
      onSearch={setSearchQuery}
      searchPlaceholder="Search make, model, caliber, serial..."
    >
      <div className="space-y-6" data-testid="firearms-page">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-3">
            {/* Type Filter */}
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-36 rounded-sm" data-testid="filter-type">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {FIREARM_TYPES.map(type => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Caliber Filter */}
            <Select value={caliberFilter} onValueChange={setCaliberFilter}>
              <SelectTrigger className="w-36 rounded-sm" data-testid="filter-caliber">
                <SelectValue placeholder="Caliber" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Calibers</SelectItem>
                {allCalibers.map(caliber => (
                  <SelectItem key={caliber} value={caliber}>{caliber}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Tag Filter */}
            {uniqueTags.length > 0 && (
              <Select value={tagFilter} onValueChange={setTagFilter}>
                <SelectTrigger className="w-36 rounded-sm" data-testid="filter-tag">
                  <SelectValue placeholder="Tag" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tags</SelectItem>
                  {uniqueTags.map(tag => (
                    <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-muted-foreground"
                data-testid="clear-filters"
              >
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
            )}
          </div>

          <div className="flex items-center gap-3">
            {/* View Toggle */}
            <div className="flex items-center border border-border rounded-sm">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 ${viewMode === 'grid' ? 'bg-secondary text-secondary-foreground' : 'hover:bg-muted'}`}
                data-testid="view-grid"
              >
                <Grid3X3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 ${viewMode === 'list' ? 'bg-secondary text-secondary-foreground' : 'hover:bg-muted'}`}
                data-testid="view-list"
              >
                <List className="w-4 h-4" />
              </button>
            </div>

            <Button
              onClick={() => navigate('/firearms/new')}
              className="bg-secondary hover:bg-secondary/90 rounded-sm btn-tactical"
              data-testid="add-firearm-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Firearm
            </Button>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4' : 'space-y-3'}>
            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
              <div key={i} className="bg-card border border-border rounded-sm p-4 animate-pulse">
                <div className="aspect-[4/3] bg-muted rounded-sm mb-4" />
                <div className="h-4 bg-muted rounded mb-2 w-3/4" />
                <div className="h-3 bg-muted rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : firearms.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Target className="w-16 h-16 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Firearms Found</h3>
            <p className="text-muted-foreground mb-6 max-w-md">
              {hasActiveFilters || searchQuery
                ? 'No firearms match your current filters. Try adjusting your search criteria.'
                : 'Start building your inventory by adding your first firearm.'}
            </p>
            {!hasActiveFilters && !searchQuery && (
              <Button
                onClick={() => navigate('/firearms/new')}
                className="bg-secondary hover:bg-secondary/90 rounded-sm btn-tactical"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Firearm
              </Button>
            )}
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {firearms.map((firearm, index) => (
              <div
                key={firearm.id}
                className="bg-card border border-border rounded-sm overflow-hidden cursor-pointer card-hover animate-fade-in"
                style={{ animationDelay: `${index * 0.05}s` }}
                onClick={() => navigate(`/firearms/${firearm.id}`)}
                data-testid={`firearm-card-${firearm.id}`}
              >
                {/* Image - Now using EntityThumbnail for object URL rendering */}
                <EntityThumbnail
                  imageFileIds={firearm.images}
                  alt={`${firearm.manufacturer} ${firearm.model}`}
                  containerClassName="aspect-[4/3] bg-muted overflow-hidden"
                  className="w-full h-full object-cover"
                  fallback={
                    <div className="w-full h-full flex items-center justify-center">
                      <Target className="w-12 h-12 text-muted-foreground/30" />
                    </div>
                  }
                />

                {/* Content */}
                <div className="p-4">
                  <h3 className="font-semibold text-foreground truncate">
                    {firearm.manufacturer} {firearm.model}
                  </h3>
                  <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                    <span>{firearm.type}</span>
                    <span className="text-border">•</span>
                    <span>{firearm.caliber}</span>
                  </div>

                  {/* Tags */}
                  {firearm.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3">
                      {firearm.tags.slice(0, 3).map(tag => (
                        <Badge
                          key={tag}
                          variant="outline"
                          className={`text-xs rounded-sm ${getTagColor(tag)}`}
                        >
                          {tag}
                        </Badge>
                      ))}
                      {firearm.tags.length > 3 && (
                        <Badge variant="outline" className="text-xs rounded-sm">
                          +{firearm.tags.length - 3}
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {firearms.map((firearm, index) => (
              <div
                key={firearm.id}
                className="bg-card border border-border rounded-sm p-4 flex items-center gap-4 cursor-pointer card-hover animate-fade-in"
                style={{ animationDelay: `${index * 0.03}s` }}
                onClick={() => navigate(`/firearms/${firearm.id}`)}
                data-testid={`firearm-row-${firearm.id}`}
              >
                {/* List view thumbnail - using EntityThumbnail */}
                <EntityThumbnail
                  imageFileIds={firearm.images}
                  alt=""
                  containerClassName="w-16 h-12 bg-muted rounded-sm overflow-hidden flex-shrink-0"
                  className="w-full h-full object-cover"
                  fallback={
                    <div className="w-full h-full flex items-center justify-center">
                      <Target className="w-6 h-6 text-muted-foreground/30" />
                    </div>
                  }
                />

                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-foreground truncate">
                    {firearm.manufacturer} {firearm.model}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {firearm.type} • {firearm.caliber}
                  </p>
                </div>

                {firearm.tags?.length > 0 && (
                  <div className="hidden sm:flex flex-wrap gap-1">
                    {firearm.tags.slice(0, 2).map(tag => (
                      <Badge
                        key={tag}
                        variant="outline"
                        className={`text-xs rounded-sm ${getTagColor(tag)}`}
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
